import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { clampStickVector, type ControlMode, type StickVector } from "./control-types";
import { DualStickGamepad } from "./dual-stick-gamepad";
import { useKeyboardVector, type KeyboardVectorBindings } from "./useKeyboardVector";
import { MarbleControls } from "./marble-controls";
import { MarbleControlsWrapper } from "./marble-controls-wrapper";
import { MarbleControlsContext } from "./controls-context";
import { TorchcamCameraData } from "@/transforms";
import { SceneLoaderHook } from "@/hooks/useSceneLoader";

declare global {
  interface Window {
    __rtfmControlBridge?: {
      setFromServer?: (cameraData: unknown) => void;
      setMode?: (mode: ControlMode) => void;
      getMode?: () => ControlMode;
    };
  }
}

type ControlsInputContextValue = {
  mode: ControlMode;
  setMode: (mode: ControlMode) => void;
  sticks: {
    move: StickVector;
    look: StickVector;
  };
  keyboard: {
    move: StickVector;
    look: StickVector;
  };
  pointer: {
    move: StickVector;
    look: StickVector;
    displayMove: StickVector;
    displayLook: StickVector;
    setMove: (value: StickVector) => void;
    setLook: (value: StickVector) => void;
    setMoveActive: (active: boolean) => void;
    setLookActive: (active: boolean) => void;
    isMoveActive: boolean;
    isLookActive: boolean;
  };
};

export const ControlsInputContext = createContext<ControlsInputContextValue | null>(null);

export const useControlsInput = () => {
  const value = useContext(ControlsInputContext);
  if (!value) {
    throw new Error("useControlsInput must be used within ControlsProvider");
  }
  return value;
};

const MOVE_BINDINGS: KeyboardVectorBindings = {
  // do not use arrow keys for movement, they are reserved for look
  horizontal: {
    positive: ["KeyD"],
    negative: ["KeyA"],
  },
  vertical: {
    positive: ["KeyS"],
    negative: ["KeyW"],
  },
};

const LOOK_BINDINGS: KeyboardVectorBindings = {
  horizontal: {
    positive: ["ArrowRight"],
    negative: ["ArrowLeft"],
  },
  vertical: {
    positive: ["ArrowDown"],
    negative: ["ArrowUp"],
  },
};

export const ControlsProvider = ({ children }: { children?: ReactNode }) => {

  const [mode, setMode] = useState<ControlMode>("fps");

  const [pointerMove, setPointerMove] = useState<StickVector>({ x: 0, y: 0 });
  const [pointerLook, setPointerLook] = useState<StickVector>({ x: 0, y: 0 });
  const [isMoveActive, setMoveActive] = useState(false);
  const [isLookActive, setLookActive] = useState(false);

  const keyboardMove = useKeyboardVector(MOVE_BINDINGS);
  const keyboardLook = useKeyboardVector(LOOK_BINDINGS);

  const effectiveMove = useMemo(() => {
    const keyboardContribution = isMoveActive ? { x: 0, y: 0 } : keyboardMove;
    const combined = clampStickVector({
      x: pointerMove.x + keyboardContribution.x,
      y: pointerMove.y + keyboardContribution.y,
    });
    return combined;
  }, [keyboardMove, pointerMove, isMoveActive]);

  const effectiveLook = useMemo(() => {
    const keyboardContribution = isLookActive ? { x: 0, y: 0 } : keyboardLook;
    const combined = clampStickVector({
      x: pointerLook.x + keyboardContribution.x,
      y: pointerLook.y + keyboardContribution.y,
    });
    return combined;
  }, [keyboardLook, pointerLook, isLookActive]);

  const displayMove = useMemo(() => {
    if (isMoveActive) {
      return pointerMove;
    }
    return clampStickVector({
      x: pointerMove.x + keyboardMove.x,
      y: pointerMove.y + keyboardMove.y,
    });
  }, [isMoveActive, pointerMove, keyboardMove]);

  const displayLook = useMemo(() => {
    if (isLookActive) {
      return pointerLook;
    }
    return clampStickVector({
      x: pointerLook.x + keyboardLook.x,
      y: pointerLook.y + keyboardLook.y,
    });
  }, [isLookActive, pointerLook, keyboardLook]);

  const handlePointerMove = useCallback((next: StickVector) => {
    setPointerMove(clampStickVector(next));
  }, []);

  const handlePointerLook = useCallback((next: StickVector) => {
    setPointerLook(clampStickVector(next));
  }, []);

  const lastCursorLocked = useRef<boolean>(false);

  // TODO: reintroduce proper cursor locking 
  useEffect(() => {
    const keyboardActive =
      Math.abs(keyboardMove.x) > 0 ||
      Math.abs(keyboardMove.y) > 0 ||
      Math.abs(keyboardLook.x) > 0 ||
      Math.abs(keyboardLook.y) > 0;
    const locked = isMoveActive || isLookActive || keyboardActive;
    if (lastCursorLocked.current !== locked) {
      lastCursorLocked.current = locked;
      //TODO: dispatch cursorLocked without causing a rerender
      // dispatchAppState({ cursorLocked: locked });
    }
  }, [isMoveActive, isLookActive, keyboardMove, keyboardLook]);

  const contextValue = useMemo<ControlsInputContextValue>(() => {
    return {
      mode,
      setMode,
      sticks: {
        move: effectiveMove,
        look: effectiveLook,
      },
      keyboard: {
        move: keyboardMove,
        look: keyboardLook,
      },
      pointer: {
        move: pointerMove,
        look: pointerLook,
        displayMove,
        displayLook,
        setMove: handlePointerMove,
        setLook: handlePointerLook,
        setMoveActive: setMoveActive,
        setLookActive: setLookActive,
        isMoveActive,
        isLookActive,
      },
    };
  }, [
    mode,
    effectiveMove,
    effectiveLook,
    keyboardMove,
    keyboardLook,
    pointerMove,
    pointerLook,
    displayMove,
    displayLook,
    handlePointerMove,
    handlePointerLook,
    isMoveActive,
    isLookActive,
  ]);

  return (
    <ControlsInputContext.Provider value={contextValue}>
      {children}
    </ControlsInputContext.Provider>
  );
};

export const ControlsContextBridge = ({ children }: { children?: ReactNode }) => {
  const context = useControlsInput();
  return (
    <ControlsInputContext.Provider value={context}>
      {children}
    </ControlsInputContext.Provider>
  );
};

export const ControlsCanvasBridge = ({ children }: { children?: ReactNode }) => {
  const { sticks, mode, setMode } = useControlsInput();
  const controls = useMemo(() => new MarbleControls(mode), []);

  useEffect(() => {
    return () => {
      controls.dispose();
    };
  }, [controls]);

  useEffect(() => {
    controls.setControlMode(mode);
  }, [controls, mode]);

  useEffect(() => {
    const bridge = {
      setFromServer: (cameraData: unknown) => {
        controls.setFromServer(cameraData as TorchcamCameraData);
      },
      setMode: (nextMode: ControlMode) => {
        setMode(nextMode);
      },
      getMode: () => mode,
    };
    window.__rtfmControlBridge = bridge;
    return () => {
      if (window.__rtfmControlBridge === bridge) {
        delete window.__rtfmControlBridge;
      }
    };
  }, [controls, mode, setMode]);

  return (
    <MarbleControlsContext.Provider value={controls}>
      <MarbleControlsWrapper
        controls={controls}
        move={sticks.move}
        look={sticks.look}
        mode={mode}
      >
        {children}
      </MarbleControlsWrapper>
    </MarbleControlsContext.Provider>
  );
};

export const ControlsPanel = ({
  className, 
  sceneLoader,
}: {
  className?: string, 
  sceneLoader: SceneLoaderHook,
}) => {
  const { mode, setMode, pointer } = useControlsInput();

  return (
    <DualStickGamepad
      className={className}
      mode={mode}
      onModeChange={setMode}
      sceneLoader={sceneLoader}
      left={{
        id: "move-stick",
        label: "Move",
        value: pointer.move,
        displayValue: pointer.displayMove,
        onChange: pointer.setMove,
        onStart: () => pointer.setMoveActive(true),
        onEnd: () => {
          pointer.setMoveActive(false);
          pointer.setMove({ x: 0, y: 0 });
        },
      }}
      right={{
        id: "look-stick",
        label: "Look",
        value: pointer.look,
        displayValue: pointer.displayLook,
        onChange: pointer.setLook,
        onStart: () => pointer.setLookActive(true),
        onEnd: () => {
          pointer.setLookActive(false);
          pointer.setLook({ x: 0, y: 0 });
        },
      }}
    />
  );
};
